import React, { Fragment, memo, useCallback, useMemo } from "react";
import type { BlockPlotModelT } from "../../../../../../../schema/block/plot";
import { hashCode } from "diagnostics-lib/object";
import { useGUIEditorContext } from "../../../../../../../hook/context/gui";
import { groupBy } from "diagnostics-lib/iterable";
import { LayerSetting } from "./LayerSetting";
import { LayerSettingHeader } from "./LayerSettingHeader";
import type { EncodingT } from "../../../../../../../schema/encoding";
import { useAvailableResources } from "../../../../../../../hook/useAvailableResources";

export const LayerSettings = memo(
  ({ projectId, layers }: { projectId: string; layers: BlockPlotModelT["config"]["layers"] }) => {
    const availableResources = useAvailableResources({ projectId });
    const { block, preset } = useGUIEditorContext();

    const groupYAxesByUnit = block?.config.options?.groupYAxesByUnit;

    const availableRelatedIdentifiers = useMemo(
      () =>
        [availableResources.quantity, availableResources.fault]
          .map(({ identifiers }) => identifiers)
          .flat(),
      [availableResources],
    );

    const defineAxisGroupEntry = useCallback(
      (layerPosition: number, encodingPosition: number, axis: string, encoding: EncodingT) => {
        const relatedIdentifier = availableRelatedIdentifiers.find(
          r => r.name === encoding.identifier,
        );
        const relatedQuantity = availableResources.performanceIndicators.find(
          r => r && r.name === encoding.quantity,
        );
        const unit =
          encoding.column !== "globalImpact" && encoding.column !== "normalizedGap"
            ? (relatedQuantity?.["unit"] ?? relatedIdentifier?.["unit"])
            : null;
        const tag =
          relatedIdentifier && "system" in relatedIdentifier ? relatedIdentifier.system : null;
        return {
          unit,
          tag,
          axis,
          layerPosition,
          encodingPosition,
          encoding,
        };
      },
      [availableRelatedIdentifiers, availableResources.performanceIndicators],
    );

    const axes = useMemo(() => {
      const entries = layers.flatMap((layer, i) =>
        Object.entries(layer.encoding)
          .filter(([key]) => (preset === "line" ? key === "y" : ["x", "y"].includes(key)))
          .map(([axis, encoding], j) => defineAxisGroupEntry(i, j, axis, encoding))
          .filter(({ encoding }) => encoding.identifier.length > 0 && encoding.column.length > 0),
      );

      if (groupYAxesByUnit && preset === "line") {
        return Array.from(groupBy(entries, "unit").entries()) as [string | null, typeof entries][];
      }

      return entries.map(e => [e.unit, [e]]) as [string | null, typeof entries][];
    }, [defineAxisGroupEntry, groupYAxesByUnit, layers, preset]);

    return axes?.flatMap(([unit, entries], i) =>
      entries.map((entry, j, arr) => (
        <Fragment key={hashCode(entry)}>
          {(preset === "scatter" || (preset === "line" && !groupYAxesByUnit)) && (
            <LayerSettingHeader
              index={i}
              position={entry.layerPosition}
              unit={unit}
              axis={entry.axis}
            />
          )}
          {preset === "line" && groupYAxesByUnit && arr.length >= 1 && j === 0 && (
            <LayerSettingHeader
              index={i}
              position={entry.layerPosition}
              unit={unit}
              axis={entry.axis}
            />
          )}
          <LayerSetting
            projectId={projectId}
            key={block?.id}
            encoding={entry.encoding}
            axis={entry.axis as "x" | "y"}
            position={entry.layerPosition}
            displayZoomOptions={groupYAxesByUnit ? j === arr.length - 1 : true}
            preset={preset}
          />
        </Fragment>
      )),
    );
  },
);
